import React, { useState, useRef } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import { FaUpload, FaTimes, FaPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { eventPhotosApi } from '../../../api/events/eventPhotosApi';

const PhotoUploadModal = ({ show, handleClose, onUploadSuccess }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setSelectedFiles(prev => [...prev, ...newFiles]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
      if (droppedFiles.length > 0) {
        setSelectedFiles(prev => [...prev, ...droppedFiles]);
      }
    }
  };

  const removeFile = (index) => {
    const newFiles = [...selectedFiles];
    newFiles.splice(index, 1);
    setSelectedFiles(newFiles);
  };

  const handleUploadClick = async () => {
    if (selectedFiles.length === 0) {
      toast.warning('Tafadhali chagua picha za kupakia (Please select photos to upload).');
      return;
    }
    setIsUploading(true);
    try {
      // Upload each file individually so the server receives a single file per request
      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append("photos", file);
        await eventPhotosApi.uploadPhotos(formData);
      }

      toast.success('Photos uploaded successfully!');
      setSelectedFiles([]);
      onUploadSuccess(); // notify parent to refresh
      handleClose();
    } catch (err) {
      console.error(err);
      toast.error('Failed to upload photos.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleModalClose = () => {
    if (!isUploading) {
      setSelectedFiles([]);
      handleClose();
    }
  };

  return (
    <Modal show={show} onHide={handleModalClose} centered backdrop="static" size="lg">
      <Modal.Header closeButton={!isUploading}>
        <Modal.Title className="fw-bold" style={{ color: '#4169e1' }}>
          Pakia Picha Zote Kwa Pamoja (Bulk Upload)
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-4">
            <Form.Label className="fw-bold">Chagua Picha Hapa (Select Photos)</Form.Label>
            <Form.Control
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileChange}
              disabled={isUploading}
              size="lg"
            />
          </Form.Group>

          {/* Clickable Dropzone Area */}
          <div
            onClick={() => !isUploading && fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`text-center p-5 border rounded transition-all ${isDragging ? 'bg-primary text-white border-primary' : 'bg-light text-muted'}`}
            style={{
              borderStyle: 'dashed',
              borderWidth: '2px',
              cursor: isUploading ? 'not-allowed' : 'pointer',
              borderColor: isDragging ? '#4169e1' : '#cbd5e1',
              backgroundColor: isDragging ? 'rgba(65, 105, 225, 0.05)' : '#f8fafc',
              transition: 'all 0.2s ease-in-out'
            }}
          >
            <FaUpload className="mb-3" size={36} style={{ color: '#4169e1' }} />
            <h5 className="fw-bold mb-1" style={{ color: '#1e293b' }}>
              Drag and drop your photos here or click to browse
            </h5>
            <p className="mt-1 mb-0 text-secondary small">
              Bonyeza mahali popote hapa au kokota picha (Click anywhere here or drag photos to select multiple)
            </p>
          </div>

          {selectedFiles.length > 0 && (
            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-bold text-dark" style={{ fontSize: '0.9rem' }}>
                  Picha zilizochaguliwa ({selectedFiles.length}):
                </span>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  style={{ borderRadius: '20px' }}
                >
                  <FaPlus className="me-1" size={11} /> Ongeza Picha Zaidi
                </Button>
              </div>
              <div className="d-flex flex-wrap gap-3 justify-content-start p-3 border rounded bg-light" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                {selectedFiles.map((file, index) => (
                  <div key={index} className="position-relative shadow-sm rounded bg-white p-1" style={{ border: '1px solid #dee2e6' }}>
                    <img
                      src={URL.createObjectURL(file)}
                      alt="preview"
                      style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '4px' }}
                    />
                    <Button
                      variant="danger"
                      size="sm"
                      className="position-absolute rounded-circle shadow d-flex justify-content-center align-items-center"
                      style={{ width: '24px', height: '24px', padding: 0, top: '-8px', right: '-8px', zIndex: 10 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(index);
                      }}
                      disabled={isUploading}
                    >
                      <FaTimes size={12} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={handleModalClose} disabled={isUploading}>
          Funga (Close)
        </Button>
        <Button
          variant="primary"
          onClick={handleUploadClick}
          disabled={isUploading || selectedFiles.length === 0}
          style={{ background: 'linear-gradient(135deg, #4169e1 0%, #3a59c0 100%)', border: 'none' }}
        >
          {isUploading ? <><Spinner size="sm" animation="border" className="me-2" /> Inapakia... (Uploading)</> : <><FaUpload className="me-2" /> Pakia Picha (Upload)</>}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PhotoUploadModal;
